// Payment method availability per tab.
//
// A single-intent session has exactly one configured intent, so "this session
// has no payment methods" and "this tab has no payment methods" are the same
// statement and the full-page empty state is correct. A multi-intent session
// must keep both tabs while at least one of them still has a method, so the
// answer is recorded per intent and the full-page state is shown only once
// every configured intent is known to be empty.
//
// The record lives at the top level of initTheme, next to the active intent,
// because that reducer merges top-level keys while it replaces the nested
// session object wholesale.

import { getPrimaryIntent, getSecondaryIntents, isMultiIntentSession } from './intentMapping';

export type IntentAvailability = Record<string, boolean>;

/**
 * Every intent the session was created with, primary first, without repeats.
 */
export const getConfiguredIntents = (state: any): string[] =>
  [getPrimaryIntent(state), ...getSecondaryIntents(state)]
    .filter((intent): intent is string => !!intent)
    .filter((intent, index, intents) => intents.indexOf(intent) === index);

export const getIntentAvailability = (state: any): IntentAvailability =>
  state?.initTheme?.intentAvailability || {};

/**
 * The configured intents whose availability has not been established yet.
 */
export const getUnknownAvailabilityIntents = (state: any): string[] => {
  const availability = getIntentAvailability(state);

  return getConfiguredIntents(state).filter((intent) => availability[intent] === undefined);
};

/**
 * True only once every configured intent is known to have no payment method.
 * An intent whose availability is still unknown keeps this false, so the
 * full-page empty state is never shown on a guess.
 */
export const isEveryConfiguredIntentEmpty = (state: any): boolean => {
  const availability = getIntentAvailability(state);
  const configuredIntents = getConfiguredIntents(state);

  return (
    configuredIntents.length > 0 &&
    configuredIntents.every((intent) => availability[intent] === false)
  );
};

/**
 * True while the tab the player is on has no payment method but another
 * configured intent still does, which is the case that must show the tab's own
 * empty state with both tabs still in place.
 */
export const isActiveTabEmptyWithAnotherTabAvailable = (state: any, activeIntent: any): boolean => {
  if (!isMultiIntentSession(state)) return false;

  const availability = getIntentAvailability(state);

  if (availability[activeIntent as string] !== false) return false;

  return getConfiguredIntents(state).some((intent) => availability[intent] === true);
};

/**
 * The amount-first rule for the tab's own empty state: the tab the player is
 * on is known to have no payment method, and not every configured intent is
 * known to be empty. An intent whose availability is still unknown counts as
 * "may have methods", because amount-first asks for a payment method list only
 * once an amount exists, so the other tab may stay unknown until the player
 * opens it. Once every intent is known to be empty, the full-page empty state
 * takes over instead.
 */
export const isActiveTabEmptyOnAmountFirst = (state: any, activeIntent: any): boolean => {
  if (!isMultiIntentSession(state)) return false;

  if (getIntentAvailability(state)[activeIntent as string] !== false) return false;

  return !isEveryConfiguredIntentEmpty(state);
};
